import { useSubscriptionsStore } from "@shared/stores/subscriptions";
import { useAuthStore } from "@shared/stores/auth";
import { rolesService } from "@shared/services/roles";
import { useEngine } from "@shared/use/useEngine.ts";
import { getHeapClient } from "@shared/module-transformers/getHeapClient.ts";
import { HeapMethods } from "@shared/types/global.ts";
import { UserDTO } from "@shared/types/common";
import { useThemeStore } from "@shared/stores/theme.ts";
import { useNavStore } from "@shared/stores/navigation.ts";
import { usePartnerStore } from "@shared/stores/partner/partner.ts";
import { usePartnerTableStore } from "@shared/stores/partner/partnerTable.ts";
import { useKeycloakStore } from "@shared/stores/keycloak.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";

export type HeapPropertyValue = number | string | boolean | bigint;

type HeapService = {
  init: () => void;
  clearAll: () => void;
  setSubscriptionProperties: () => void;
  setUserIdentityProperties: () => void;
  track: HeapMethods["track"];
  trackLoginSuccess: (user: UserDTO) => void;
  setPartnerProperties: () => void;
  setPartnerSubscriptionsProperties: () => void;
  trackLoginFailed: (arg: {
    reason: string;
    details?: Record<string, HeapPropertyValue>;
  }) => void;
};

export const heapService: HeapService = {
  // Web: initialized automatically by the IIFE loader + window.heap.load() in index.html
  // Mobile: initialized via Heap.startRecording(window.HEAP_ID) in mobile/src/main.ts
  init: () => {},

  clearAll: () => {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }
    heap.resetIdentity();
    heap.clearEventProperties();
  },
  setPartnerSubscriptionsProperties(): void {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }
    heap.clearEventProperties();

    const partnerTableStore = usePartnerTableStore();

    heap.addEventProperties({
      ...commonHeapProps(),
      managedSubscription:
        partnerTableStore.saasSubscriptions +
        partnerTableStore.selfHostedSubscriptions,
      saasSubscription: partnerTableStore.saasSubscriptions,
      shSubscription: partnerTableStore.selfHostedSubscriptions,
    });
  },

  setSubscriptionProperties(): void {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }
    const subscriptionStore = useSubscriptionsStore();
    const authStore = useAuthStore();
    const role = rolesService.extractUserRoleForSubscription(
      subscriptionStore.currentSubscriptionNumber,
    );
    heap.clearEventProperties();
    const currentSubscription = subscriptionStore.currentSubscription;
    if (!currentSubscription) {
      return;
    }
    heap.addEventProperties({
      ...commonHeapProps(),
      subscriptionId: currentSubscription.accountNumber,
      subscriptionType: currentSubscription.paymentType,
      subscriptionState: currentSubscription.state,
      isTrialSubscription: currentSubscription.trial,
      hasBusinessSecurity: currentSubscription.hasBusinessSecurity,
      paymentType: currentSubscription.meta.paymentTypeName,
      isSaas: currentSubscription.meta.isSaas || "undefined",
      isSelfHosted: currentSubscription.meta.isSelfHosted || "undefined",
      role: role || "undefined",
      shBomType: currentSubscription.bom?.type || "undefined",
      loginType: useSessionLogic().getSessionType() || "undefined",
      platformIdentification: authStore.user?.platformIdentification || "N/A",
    });
  },
  setPartnerProperties(): void {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }

    const partnerStore = usePartnerStore();
    const email = useKeycloakStore().tokenPayload?.email || "";

    heap.addUserProperties({
      ...commonHeapProps(),
      partnerId: partnerStore.currentPartnerId,
      partnerName: partnerStore.selectedPartnershipName,
      userId: useKeycloakStore().tokenPayload?.sub || "N/A",
      isInternalJfrogUser:
        (email?.includes("@jfrog.com") && email?.includes("impersonate")) ||
        false,
    });
  },

  setUserIdentityProperties(): void {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }
    const authStore = useAuthStore();
    if (!authStore.user) {
      return;
    }
    heap.resetIdentity();
    heap.identify(authStore.user.heapIdentity);
    const isInternalJfrogUser = authStore.user.email
      .toLowerCase()
      .includes("@jfrog.com");

    heap.addUserProperties({
      ...commonHeapProps(),
      isInternalJfrogUser,
      loginType: useSessionLogic().getSessionType() || "undefined",
    });
  },
  track: (event, properties, timestamp, sourceInfo) => {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }

    const navStore = useNavStore();

    const eventProperties = {
      ...commonHeapProps(),
      ...(properties || {}),
      menu: navStore.activeMenu?.name || "unknown",
      viewTabName: navStore.viewTabName || "unknown",
    };

    heap.track(event, eventProperties, timestamp, sourceInfo);
  },
  trackLoginSuccess: (user: UserDTO) => {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }
    heap.track("LOGIN_SUCCESS", {
      ...commonHeapProps(),
      heapIdentity: user.heapIdentity,
      isInternalJfrogUser: user.email.includes("@jfrog.com"),
      loginType: useSessionLogic().getSessionType() || "undefined",
    });
  },

  trackLoginFailed: ({ reason, details }) => {
    const heap = getHeapClient();
    if (!heap) {
      return;
    }
    heap.track("LOGIN_FAILED", {
      ...commonHeapProps(),
      loginType: useSessionLogic().getSessionType() || "undefined",
      reason,
      ...details,
    });
  },
};

const commonHeapProps = () => ({
  myjfrogEngine: useEngine().engine,
  theme: useThemeStore().activeTheme,
  screenWidth: window.screen.width,
  screenHeight: window.screen.height,
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
});
